/**
 * Art-lab staging of the fight key frames (art §10.2): `fight` = the engaged heavy hit (hit-stop frozen, ink splats
 * hanging in the air); `fight-rest` = both mains on their rest marks with a gentle gothic sway. Uses the engine's
 * test meshes, or a `CreatureSource` (P1's generator) when one is injected. Roots follow architecture §5.8:
 * player T·R_y(+70°), opponent T·R_y(−70°)·S(−1, 1, 1).
 */
import type { ActorFrame, CreatureDesc, CreatureHandle, CreatureLookData, CreatureMeshData, PropHandle, Renderer, Rig, Vec3 } from '../../shared';
import { m4FromRTS, m4Mul, qFromEulerYXZ } from '../../shared';
import { FUR_PALETTES, TOKENS, packCreatureLook, type FurPalette } from '../../content/palette';
import { bipedRig, newActorFrame, Poser, snailRig, type PoseSpec, type RootSpec } from '../testmesh/rig';
import { buildCat, buildFishSword, buildLance, buildShield, buildSnail } from '../testmesh/creatures';

/** Optional provider of real creatures (P1). Returning null keeps the engine's test meshes. */
export interface CreatureSource {
  prepare(role: 'hero' | 'foe'): { rig: Rig; mesh: CreatureMeshData; look: CreatureLookData } | null;
}

export type KeyframeId = 'fight' | 'fight-rest';

/** The keyart tabby: a warm buff mackerel tabby (palette id overridable with `?fur=`). */
let TABBY_PALETTE = 'ochreFur';
export const setTabbyPalette = (id: string): void => { TABBY_PALETTE = id; };

const SNAIL_FUR: FurPalette = { id: 'snailBody', ru: 'Улиточий', base: '#A8B39A', stripe: TOKENS.verdaccio, belly: '#D8D0C0', stroke: TOKENS.inkFaded, cov: 0.25 };

interface Actor { handle: CreatureHandle; poser: Poser; frame: ActorFrame; rig: Rig; root: RootSpec; kind: 'cat' | 'snail' }

const m16 = (): Float32Array => new Float32Array(16);

/** Static poses (creature-local Euler YXZ degrees; creatures §B1 signs). */
const CAT_STRIKE: PoseSpec = {
  pelvis: { r: [0, -14, 0], t: [0, -0.03, 0] }, spine_01: [8, -12, 0], spine_02: [6, -10, -2], neck: [-6, -6, 0], head: [-4, -14, 7],
  clavicle_R: [0, 10, -6], upperarm_R: [-60, 30, -22], forearm_R: [-62, 0, 0], hand_R: [30, 6, 0],
  clavicle_L: [0, 0, 2], upperarm_L: [-18, 0, 8], forearm_L: [-40, 0, 0], hand_L: [10, 0, 0],
  thigh_L: [-36, 0, 6], calf_L: [38, 0, 0], foot_L: [-2, 0, 0], thigh_R: [26, 0, -6], calf_R: [24, 0, 0], foot_R: [-32, 0, 0],
  tail_1: [-6, 10, 0], tail_2: [-4, -8, 0], tail_3: [6, 6, 0], tail_4: [10, 0, 0], cloth_0: [12, 0, 0],
  ear_L_01: [0, 0, 8], ear_R_01: [0, 0, -8],
};
const CAT_REST: PoseSpec = {
  pelvis: { r: [0, -12, 0], t: [0.0, -0.008, 0] }, spine_01: [-3, -8, 2], spine_02: [-2, -8, 0], neck: [2, -8, 0], head: [6, -14, 6],
  upperarm_R: [-20, 0, -8], forearm_R: [-70, 0, 0], hand_R: [40, 0, 0],
  upperarm_L: [8, 0, 10], forearm_L: [-22, 0, 0],
  thigh_L: [-12, 0, 3], calf_L: [12, 0, 0], foot_L: [0, 0, 0], thigh_R: [10, 0, -3], calf_R: [6, 0, 0], foot_R: [-8, 0, 0],
  tail_2: [0, 6, 0], tail_3: [4, -4, 0],
};
const SNAIL_HIT: PoseSpec = {
  spine_01: [-4, 0, 0], spine_02: [-14, 0, 4], neck: [-12, 0, -4], head: [-10, 12, -6],
  ear_L_01: [-18, 0, 22], ear_L_02: [-12, 0, 10], ear_R_01: [-26, 0, -14], ear_R_02: [-16, 0, -8], shell: [0, 0, 4],
};
const SNAIL_REST: PoseSpec = {
  spine_02: [-4, 0, 0], neck: [-4, 0, 0], head: [0, 10, 0], ear_L_01: [-8, 0, 12], ear_R_01: [-8, 0, -12], ear_L_02: [-6, 0, 6], ear_R_02: [-6, 0, -6],
};

export class FightStage {
  readonly actors: Actor[] = [];
  readonly props: Array<{ handle: PropHandle; owner: Actor; attach: (a: Actor, out: Float32Array) => void }> = [];
  private readonly tmp = m16();
  contactPoint: Vec3 = [0, 0.6, 0];
  /** Where the fish sword and the lance cross (engaged key frame). */
  clash: Vec3 = [0, 0.9, 0.1];

  constructor(private r: Renderer, readonly kf: KeyframeId, source?: CreatureSource | null) {
    const engaged = kf === 'fight';
    const heroX = engaged ? -0.36 : -0.5;
    const foeX = engaged ? 0.33 : 0.5;
    // player: tabby crusader
    const hero = source?.prepare('hero') ?? null;
    const catRig = hero?.rig ?? bipedRig();
    const catLook = hero?.look ?? packCreatureLook({ fur: FUR_PALETTES.find((p) => p.id === (TABBY_PALETTE))!, pattern: 'mackerel', dyes: [TOKENS.leadWhite, TOKENS.vermilion, TOKENS.lapisLight, TOKENS.gold], iris: '#C99A2E', seed: 17, headFrame: [0, 0.845, 0.04, 0.155] });
    this.actors.push(this.add({ mesh: hero?.mesh ?? buildCat(catRig), rig: catRig, look: catLook, role: 'main' }, { x: heroX, z: 0.02, yawDeg: 70 }, 'cat'));
    // opponent: snail knight
    const foe = source?.prepare('foe') ?? null;
    const snRig = foe?.rig ?? snailRig();
    const snLook = foe?.look ?? packCreatureLook({ fur: SNAIL_FUR, pattern: 'solid', dyes: [TOKENS.leadWhite, TOKENS.vermilion, TOKENS.lapisLight, TOKENS.gold], iris: '#2B1D14', seed: 5 });
    this.actors.push(this.add({ mesh: foe?.mesh ?? buildSnail(snRig), rig: snRig, look: snLook, role: 'main', mirrored: true }, { x: foeX, z: -0.04, yawDeg: -70, mirror: true, scale: 0.86 }, 'snail'));
    // props: fish sword (cat right paw), lance + shield (snail)
    const sword = r.addProp(buildFishSword());
    const lance = r.addProp(buildLance());
    const shield = r.addProp(buildShield());
    const [cat, snail] = this.actors;
    if (sword) { sword.setLookFrom(cat.handle); this.props.push({ handle: sword, owner: cat, attach: (a, out) => a.poser.socket('prop_R', out, [90, 0, 0]) }); }
    if (lance) {
      lance.setLookFrom(snail.handle);
      this.props.push({
        handle: lance, owner: snail,
        attach: (a, out) => {
          // couched along the snail's camera side, aimed at the clash point (engaged) or held upright (rest)
          const base = a.poser.boneOrigin(a.rig.index.spine_02 ?? 0);
          const b: Vec3 = [base[0] + 0.12, base[1] - 0.06, base[2] + 0.17];
          const tgt: Vec3 = engaged ? [this.clash[0] - 0.22, this.clash[1] + 0.26, this.clash[2] + 0.06] : [b[0] - 0.25, b[1] + 1, b[2]];
          return aimProp(b, tgt, 0.82, out);
        },
      });
    }
    if (shield) { shield.setLookFrom(snail.handle); this.props.push({ handle: shield, owner: snail, attach: (a, out) => attach(a.poser, a.rig.index.spine_02 ?? 0, [-0.12, -0.02, 0.06], [0, -74, engaged ? 6 : 0], 0.62, out) }); }
  }

  private add(desc: CreatureDesc, root: RootSpec, kind: 'cat' | 'snail'): Actor {
    const handle = this.r.addCreature(desc);
    if (!handle) throw new Error('no creature slot');
    if (desc.mirrored) handle.setMirrored(true);
    return { handle, poser: new Poser(desc.rig), frame: newActorFrame(desc.rig), rig: desc.rig, root, kind };
  }

  /** Poses every actor at time t (s); the `fight` key frame ignores t (hit-stop hold). */
  update(t: number): void {
    const engaged = this.kf === 'fight';
    for (const a of this.actors) {
      const sway = engaged ? 0 : Math.sin(t * 1.1 + (a.kind === 'cat' ? 0 : 1.7));
      let spec: PoseSpec;
      if (a.kind === 'cat') {
        spec = engaged ? CAT_STRIKE : withSway(CAT_REST, sway);
      } else {
        spec = engaged ? SNAIL_HIT : withSway(SNAIL_REST, sway * 0.6);
      }
      a.poser.pose(spec, a.root, a.frame);
      // ground the lowest foot (plantigrade feet stay on the floor)
      if (a.kind === 'cat') {
        const ix = a.rig.index;
        const fl = a.poser.boneOrigin(ix.foot_L ?? 0)[1], fr = a.poser.boneOrigin(ix.foot_R ?? 0)[1];
        const dy = 0.045 * (a.root.scale ?? 1) - Math.min(fl, fr);
        if (Math.abs(dy) > 1e-4) { a.root.y = (a.root.y ?? 0) + dy; a.poser.pose(spec, a.root, a.frame); }
      }
      this.contacts(a);
      a.handle.setFrame(a.frame);
    }
    // the clash point: the fish's head (the ink bursts from it, the lance is aimed at it)
    const sw = this.props[0];
    if (sw) {
      const m = this.tmp;
      sw.attach(sw.owner, m);
      const y = 0.52;
      this.contactPoint = [m[4] * y + m[12], m[5] * y + m[13], m[6] * y + m[14]];
      this.clash = [m[4] * 0.42 + m[12], m[5] * 0.42 + m[13], m[6] * 0.42 + m[14]];
    }
    for (const p of this.props) {
      p.attach(p.owner, this.tmp);
      p.handle.setMatrix(this.tmp);
    }
  }

  private contacts(a: Actor): void {
    const c = a.frame.contacts;
    const ix = a.rig.index;
    if (a.kind === 'cat') {
      const l = a.poser.boneOrigin(ix.foot_L ?? 0), r = a.poser.boneOrigin(ix.foot_R ?? 0);
      const toeL = a.poser.boneOrigin(ix.toe_L ?? 0), toeR = a.poser.boneOrigin(ix.toe_R ?? 0);
      c[0] = (l[0] + toeL[0]) / 2; c[1] = (l[2] + toeL[2]) / 2; c[2] = Math.max(0, l[1] - 0.045); c[3] = 0.13;
      c[4] = (r[0] + toeR[0]) / 2; c[5] = (r[2] + toeR[2]) / 2; c[6] = Math.max(0, r[1] - 0.045); c[7] = 0.13;
    } else {
      const p = a.poser.boneOrigin(ix.pelvis ?? 0), s = a.poser.boneOrigin(ix.spine_01 ?? 0), t = a.poser.boneOrigin(ix.tail_1 ?? 0);
      c[0] = (p[0] + s[0]) / 2; c[1] = (p[2] + s[2]) / 2; c[2] = 0; c[3] = 0.24;
      c[4] = t[0]; c[5] = t[2]; c[6] = 0; c[7] = 0.16;
    }
  }

  dispose(): void {
    for (const p of this.props) p.handle.dispose();
    for (const a of this.actors) a.handle.dispose();
  }
}

function withSway(base: PoseSpec, s: number): PoseSpec {
  const out: PoseSpec = { ...base };
  const sp = base.spine_01;
  const r = Array.isArray(sp) ? sp : [0, 0, 0];
  out.spine_01 = [r[0] + s * 1.2, r[1], r[2] + s * 1.5];
  const hd = base.head;
  const h = Array.isArray(hd) ? hd : [0, 0, 0];
  out.head = [h[0] + s * 1.5, h[1], h[2] - s * 2];
  return out;
}

const tq = new Float32Array(4), tl = new Float32Array(16);
/** A prop matrix whose +Y runs from `base` towards `target` (uniform `scale`), flat side facing the camera. */
function aimProp(base: Vec3, target: Vec3, scale: number, out: Float32Array): Float32Array {
  let yx = target[0] - base[0], yy = target[1] - base[1], yz = target[2] - base[2];
  const yl = Math.hypot(yx, yy, yz) || 1; yx /= yl; yy /= yl; yz /= yl;
  // x = y × (0, 0, 1)
  let xx = yy, xy = -yx, xz = 0;
  const xl = Math.hypot(xx, xy, xz) || 1; xx /= xl; xy /= xl; xz /= xl;
  const zx = xy * yz - xz * yy, zy = xz * yx - xx * yz, zz = xx * yy - xy * yx;
  out.set([xx * scale, xy * scale, xz * scale, 0, yx * scale, yy * scale, yz * scale, 0, zx * scale, zy * scale, zz * scale, 0, base[0], base[1], base[2], 1]);
  return out;
}

/** Attaches a prop to a bone: boneWorld · T(offset) · R(rot). */
function attach(p: Poser, bone: number, offset: [number, number, number], rotDeg: [number, number, number], scale: number, out: Float32Array): Float32Array {
  qFromEulerYXZ(tq, rotDeg[0], rotDeg[1], rotDeg[2]);
  m4FromRTS(tl, tq, offset, [scale, scale, scale]);
  return m4Mul(out, p.boneWorld(bone), tl);
}
